import { useState, useEffect } from "react";
import { DEFAULT_THEME } from "@zendeskgarden/react-theming";
import { zendesk } from "./zendesk";

type ThemeBase = "light" | "dark";

interface UseThemeReturn {
  base: ThemeBase;
  themeObject: typeof DEFAULT_THEME & { colors: { base: ThemeBase } };
}

/**
 * Tracks the active Zendesk color scheme and adapts the Garden theme so embedded views match the host product.
 *
 * Use this hook at the app shell boundary before rendering components that depend on Garden theme tokens.
 *
 * @see https://developer.zendesk.com/documentation/apps/app-developer-guide/dark-mode/
 *
 * @returns The current Zendesk color base and the derived Garden theme object.
 */
export function useTheme(): UseThemeReturn {
  const queryParams = new URLSearchParams(window.location.search);
  const initialColorScheme = queryParams.get("colorScheme");

  const [base, setBase] = useState<ThemeBase>(initialColorScheme === "dark" ? "dark" : "light");

  useEffect(() => {
    void zendesk.get("colorScheme").then((data: { colorScheme?: string }) => {
      if (data?.colorScheme === "dark" || data?.colorScheme === "light") {
        setBase(data.colorScheme);
      }
    });

    zendesk.on?.("colorScheme.changed", (newColorScheme: string) => {
      if (newColorScheme === "dark" || newColorScheme === "light") {
        setBase(newColorScheme);
      }
    });
  }, []);

  const themeObject = {
    ...DEFAULT_THEME,
    colors: { ...DEFAULT_THEME.colors, base },
  };

  return { base, themeObject };
}
